/**
 * Additional Tax & GST Services Content
 */

// ==================== GST SERVICES ====================

export const gstLutFiling = {
  id: "gst-lut-filing",
  slug: "gst-lut-filing",
  seoTitle: "GST LUT Filing Online | Letter of Undertaking for Export",
  seoDescription: "File GST LUT for export without payment of IGST. Annual LUT filing for exporters of goods and services. Expert CA assistance for zero-rated supplies.",
  heroTitle: "GST LUT Filing",
  heroSubtitle: "Tax & GST",
  summary: "Letter of Undertaking (LUT) allows exporters to make exports without payment of integrated GST (IGST). Instead of paying IGST and then claiming refund, exporters with LUT can export goods/services without any GST payment. LUT must be filed annually and is essential for smooth export operations.",
  
  whoNeeds: {
    title: "Who Needs GST LUT?",
    description: "Exporters wanting to export without paying IGST.",
    points: [
      "Exporters of goods to foreign countries",
      "IT/software service exporters",
      "Consultants providing services to foreign clients",
      "Manufacturers exporting finished products",
      "Trading companies engaged in exports",
      "SEZ suppliers (deemed exports)",
      "Freelancers earning in foreign currency",
      "E-commerce sellers exporting products"
    ]
  },
  
  whyRequired: {
    title: "Why File LUT for Exports?",
    description: "LUT provides significant cash flow benefits for exporters.",
    points: [
      "No IGST Payment: Export without paying IGST upfront.",
      "Cash Flow: Avoid blocking funds in GST refund process.",
      "Faster Exports: No waiting for refunds before next shipment.",
      "Simple Process: Annual filing, valid for entire financial year.",
      "Competitive Pricing: Better export pricing without GST cost.",
      "Working Capital: Free up capital for business operations."
    ]
  },
  
  benefits: [
    { title: "Zero GST on Exports", description: "Make exports without any GST payment." },
    { title: "Better Cash Flow", description: "No funds blocked in refund claims." },
    { title: "Annual Validity", description: "One filing covers entire financial year." },
    { title: "Quick Processing", description: "Same-day filing and acceptance." },
    { title: "Self-Declaration", description: "No approval required, self-declaration basis." },
    { title: "All Exports Covered", description: "Covers goods, services, and deemed exports." },
    { title: "Compliance Support", description: "Ensure export invoicing compliance." },
    { title: "Renewal Reminders", description: "Timely reminders for annual renewal." }
  ],
  
  documentsRequired: {
    title: "Documents for LUT Filing",
    description: "Minimal documentation for LUT filing.",
    directors: [
      "Authorized signatory details",
      "Digital Signature Certificate",
      "Identity proof"
    ],
    company: [
      "GST registration certificate",
      "IEC Code (for goods export)",
      "Bank details with cancelled cheque",
      "Self-declaration in LUT format",
      "Previous year LUT (if renewal)"
    ]
  },
  
  process: [
    { step: 1, title: "Eligibility Check", description: "Verify exporter eligibility for LUT (no tax defaults).", duration: "Same day" },
    { step: 2, title: "Document Preparation", description: "Prepare self-declaration and supporting documents.", duration: "1 hour" },
    { step: 3, title: "GST Portal Filing", description: "File LUT on GST portal in Form GST RFD-11.", duration: "30 minutes" },
    { step: 4, title: "DSC Authentication", description: "Sign with authorized DSC.", duration: "5 minutes" },
    { step: 5, title: "ARN Generation", description: "Receive Application Reference Number (ARN).", duration: "Immediate" },
    { step: 6, title: "LUT Active", description: "LUT becomes active for export without IGST.", duration: "Immediate" }
  ],
  
  timeline: {
    total: "Same day processing",
    breakdown: [
      { phase: "Document preparation", duration: "1-2 hours" },
      { phase: "GST portal filing", duration: "30 minutes" },
      { phase: "Activation", duration: "Immediate" }
    ],
    note: "LUT valid for entire financial year. File before first export of the year."
  },
  
  pricing: {
    title: "LUT Filing Fees",
    description: "Annual LUT filing services.",
    packages: [
      {
        name: "LUT Filing",
        price: "₹999",
        description: "Annual LUT",
        features: [
          "Form preparation",
          "GST portal filing",
          "DSC assistance",
          "ARN delivery",
          "Renewal reminder"
        ]
      },
      {
        name: "LUT + IEC",
        price: "₹2,999",
        description: "Both registrations",
        features: [
          "LUT filing",
          "IEC registration",
          "Export documentation",
          "Compliance guide",
          "Year-round support"
        ],
        popular: true
      },
      {
        name: "Export Package",
        price: "₹5,999",
        description: "Complete export setup",
        features: [
          "LUT + IEC + AD Code",
          "Export invoice format",
          "Shipping bill guidance",
          "Bank documentation",
          "Ongoing support"
        ]
      }
    ],
    governmentFees: "No government fees for LUT filing. IEC: ₹500 (if included)."
  },
  
  faqs: [
    { question: "Who is not eligible for LUT?", answer: "Taxpayers prosecuted for tax evasion over ₹2.5 crore cannot file LUT. They must furnish bond with bank guarantee instead." },
    { question: "What is validity of LUT?", answer: "LUT is valid for one financial year (April to March). Fresh LUT required at start of each financial year." },
    { question: "Can I file LUT mid-year?", answer: "Yes, LUT can be filed anytime. It's valid from filing date till March 31 of that year." },
    { question: "What if I forget to file LUT?", answer: "Exports made without LUT will require IGST payment. You'll then need to claim refund through regular refund process." },
    { question: "Is LUT required for service exports?", answer: "Yes, service exports also need LUT to export without IGST. Same process applies for goods and services." },
    { question: "What about bond with bank guarantee?", answer: "Bond with bank guarantee is alternative to LUT, required only if taxpayer is ineligible for LUT. Bond amount is 15% of expected export value." }
  ],
  
  relatedServices: [
    { id: "gst-registration", label: "GST Registration" },
    { id: "iec-code", label: "IEC Code" },
    { id: "gst-return-filing", label: "GST Returns" },
    { id: "gst-refund", label: "GST Refund" },
    { id: "ad-code-registration", label: "AD Code" },
    { id: "export-documentation", label: "Export Documentation" }
  ],
  
  trustSignals: {
    incorporations: "8,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "GST Experts"
  }
};

export const gstAnnualReturn = {
  id: "gst-annual-return",
  slug: "gst-annual-return",
  seoTitle: "GSTR-9 Annual Return Filing | GST Annual Return India",
  seoDescription: "File GST annual return GSTR-9 before deadline. Comprehensive annual return filing for regular taxpayers. Expert CA services for accurate filing.",
  heroTitle: "GST Annual Return (GSTR-9)",
  heroSubtitle: "Tax & GST",
  summary: "GSTR-9 is the annual return that consolidates all monthly/quarterly GST returns filed during the financial year. It provides a comprehensive summary of outward supplies, inward supplies, ITC claimed, and taxes paid. Filing is mandatory for regular taxpayers with aggregate turnover above threshold.",
  
  whoNeeds: {
    title: "Who Must File GSTR-9?",
    description: "Regular GST taxpayers with turnover above threshold.",
    points: [
      "Regular taxpayers with turnover above ₹2 crore",
      "Taxpayers filing GSTR-1 and GSTR-3B",
      "Businesses with interstate supplies",
      "Companies registered under GST",
      "LLPs with GST registration",
      "Partnership firms above threshold",
      "Dealers and distributors",
      "Service providers above threshold"
    ]
  },
  
  whyRequired: {
    title: "Why File GSTR-9?",
    description: "Annual return is mandatory compliance with late fee penalties.",
    points: [
      "Legal Compliance: Mandatory under GST law for specified taxpayers.",
      "Reconciliation: Opportunity to reconcile and correct annual data.",
      "Audit Support: Forms basis for GST audit (GSTR-9C) if applicable.",
      "Avoid Penalties: Late filing attracts significant late fees.",
      "ITC Verification: Final opportunity to claim missed ITC.",
      "Assessment: Data used for departmental assessments and audits."
    ]
  },
  
  benefits: [
    { title: "Data Compilation", description: "Compile all monthly returns into annual summary." },
    { title: "Reconciliation", description: "Reconcile GSTR-1, GSTR-3B, and books of accounts." },
    { title: "ITC Verification", description: "Verify and claim any missed input tax credit." },
    { title: "Error Correction", description: "Identify and report corrections made during year." },
    { title: "Expert Review", description: "CA-reviewed filing for accuracy." },
    { title: "Timely Filing", description: "File before deadline to avoid late fees." },
    { title: "GSTR-9C Support", description: "Audit reconciliation if turnover above ₹5 crore." },
    { title: "Documentation", description: "Maintain proper documentation for future reference." }
  ],
  
  documentsRequired: {
    title: "Documents for GSTR-9 Filing",
    description: "Comprehensive data required for annual return.",
    directors: [
      "Authorized signatory credentials",
      "DSC for filing"
    ],
    company: [
      "All GSTR-1 returns filed",
      "All GSTR-3B returns filed",
      "Purchase register with GST details",
      "Sales register with GST details",
      "ITC register and reconciliation",
      "HSN-wise summary of supplies",
      "State-wise break-up of supplies",
      "Audited financials (if available)"
    ]
  },
  
  process: [
    { step: 1, title: "Data Collection", description: "Collect all GST returns, books of accounts, and reconciliations.", duration: "2-3 days" },
    { step: 2, title: "Return Reconciliation", description: "Reconcile GSTR-1 with GSTR-3B and books.", duration: "3-5 days" },
    { step: 3, title: "ITC Verification", description: "Verify ITC claimed vs eligible, identify mismatches.", duration: "2-3 days" },
    { step: 4, title: "Form Preparation", description: "Prepare GSTR-9 with all required tables and annexures.", duration: "2-3 days" },
    { step: 5, title: "Review & Validation", description: "CA review and validation of data accuracy.", duration: "1-2 days" },
    { step: 6, title: "Filing", description: "File GSTR-9 on GST portal with DSC.", duration: "1 day" }
  ],
  
  timeline: {
    total: "10-15 business days",
    breakdown: [
      { phase: "Data collection", duration: "2-3 days" },
      { phase: "Reconciliation", duration: "5-7 days" },
      { phase: "Preparation & filing", duration: "3-5 days" }
    ],
    note: "Due date typically December 31 for previous financial year. Start early for complex reconciliations."
  },
  
  pricing: {
    title: "GSTR-9 Filing Fees",
    description: "Annual return filing based on complexity.",
    packages: [
      {
        name: "Basic",
        price: "₹4,999",
        description: "Simple returns",
        features: [
          "Data compilation",
          "Basic reconciliation",
          "GSTR-9 preparation",
          "Portal filing",
          "Filing receipt"
        ]
      },
      {
        name: "Standard",
        price: "₹9,999",
        description: "Detailed reconciliation",
        features: [
          "Comprehensive data",
          "Full reconciliation",
          "ITC verification",
          "Amendment tracking",
          "Expert review"
        ],
        popular: true
      },
      {
        name: "With GSTR-9C",
        price: "₹19,999",
        description: "Annual + Audit",
        features: [
          "GSTR-9 filing",
          "GSTR-9C reconciliation",
          "CA certification",
          "Complete audit",
          "Full documentation"
        ]
      }
    ],
    governmentFees: "Late fee: ₹200/day (₹100 CGST + ₹100 SGST) capped at 0.25% of turnover."
  },
  
  faqs: [
    { question: "Who is exempt from GSTR-9?", answer: "Taxpayers with aggregate turnover up to ₹2 crore have optional filing. Composition dealers, casual taxable persons, and non-resident taxpayers are exempt." },
    { question: "What is GSTR-9C?", answer: "GSTR-9C is audit statement required for taxpayers with turnover above ₹5 crore. It's a reconciliation statement certified by CA." },
    { question: "Can I revise GSTR-9?", answer: "No, GSTR-9 cannot be revised once filed. Errors can only be corrected in next year's returns. Hence accuracy is crucial." },
    { question: "What is the late fee?", answer: "₹200 per day (₹100 CGST + ₹100 SGST) subject to maximum 0.25% of turnover in the state/UT." },
    { question: "Is ITC claim allowed in GSTR-9?", answer: "GSTR-9 is for reporting. Any missed ITC must be claimed through GSTR-3B before September of next year." },
    { question: "What if GSTR-1 and GSTR-3B don't match?", answer: "Differences must be reconciled and reported in GSTR-9. Material differences may trigger departmental scrutiny." }
  ],
  
  relatedServices: [
    { id: "gst-return-filing", label: "GST Returns" },
    { id: "gst-registration", label: "GST Registration" },
    { id: "gst-audit", label: "GST Audit" },
    { id: "income-tax-filing", label: "Tax Filing" },
    { id: "annual-filing", label: "Company Compliance" },
    { id: "virtual-cfo", label: "Virtual CFO" }
  ],
  
  trustSignals: {
    incorporations: "12,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Expert CAs"
  }
};

export const tdsFiling = {
  id: "tds-filing",
  slug: "tds-filing",
  seoTitle: "TDS Return Filing | TDS Compliance Services India",
  seoDescription: "File TDS returns on time. Quarterly TDS return filing, TDS payment, and Form 16/16A generation. Expert CA services for complete TDS compliance.",
  heroTitle: "TDS Return Filing",
  heroSubtitle: "Tax Services",
  summary: "Tax Deducted at Source (TDS) requires deductors to deduct tax at specified rates and deposit with government. Quarterly TDS returns must be filed reporting all deductions. We handle complete TDS compliance including timely deduction, payment, return filing, and certificate generation.",
  
  whoNeeds: {
    title: "Who Must File TDS Returns?",
    description: "All persons required to deduct TDS under Income Tax Act.",
    points: [
      "Companies paying salaries to employees",
      "Businesses paying contractors and professionals",
      "Persons paying rent above ₹50,000/month",
      "Buyers purchasing property above ₹50 lakh",
      "Payers of interest, commission, or brokerage",
      "Companies paying dividends",
      "Employers providing perquisites",
      "Any person liable to audit under Section 44AB"
    ]
  },
  
  whyRequired: {
    title: "Why is TDS Compliance Critical?",
    description: "TDS compliance has significant legal and financial implications.",
    points: [
      "Legal Mandate: Non-deduction or late deposit attracts penalties.",
      "Interest Liability: Late payment attracts 1-1.5% interest per month.",
      "Penalty: Non-filing penalty up to ₹200 per day.",
      "Deductee Claims: Deductees can only claim credit if TDS is deposited.",
      "Assessment Risk: Non-compliance triggers tax department scrutiny.",
      "Disallowance: Expenses can be disallowed if TDS not deducted."
    ]
  },
  
  benefits: [
    { title: "Timely Deduction", description: "Ensure correct TDS deduction at right rates." },
    { title: "Challan Payment", description: "Timely TDS payment to government." },
    { title: "Return Filing", description: "Quarterly return filing (24Q, 26Q, 27Q, 27EQ)." },
    { title: "Form 16/16A", description: "Generate and issue TDS certificates." },
    { title: "Correction Returns", description: "File corrections if errors found." },
    { title: "Lower TDS", description: "Help deductees apply for lower TDS certificates." },
    { title: "Compliance Calendar", description: "Track all TDS due dates." },
    { title: "Penalty Avoidance", description: "Avoid interest and penalties." }
  ],
  
  documentsRequired: {
    title: "Documents for TDS Filing",
    description: "Information needed for TDS return preparation.",
    directors: [
      "TAN (Tax Deduction Account Number)",
      "PAN of deductor",
      "Authorized signatory details"
    ],
    company: [
      "PAN of all deductees",
      "Payment details (amount, date, nature)",
      "TDS rate applied",
      "Challan details of TDS deposited",
      "Lower deduction certificates (if any)",
      "Previous quarter returns"
    ]
  },
  
  process: [
    { step: 1, title: "Data Collection", description: "Collect details of all payments and deductions made.", duration: "1-2 days" },
    { step: 2, title: "TDS Calculation", description: "Verify TDS rates and calculate any shortfalls.", duration: "1 day" },
    { step: 3, title: "Challan Payment", description: "Pay any pending TDS through challan.", duration: "Same day" },
    { step: 4, title: "Return Preparation", description: "Prepare quarterly return in applicable form.", duration: "1-2 days" },
    { step: 5, title: "Validation", description: "Validate return using FVU utility.", duration: "Same day" },
    { step: 6, title: "Filing", description: "Upload return on TRACES portal.", duration: "Same day" }
  ],
  
  timeline: {
    total: "3-5 business days per quarter",
    breakdown: [
      { phase: "Data collection", duration: "1-2 days" },
      { phase: "Preparation", duration: "1-2 days" },
      { phase: "Filing", duration: "1 day" }
    ],
    note: "Due dates: Q1 - July 31, Q2 - October 31, Q3 - January 31, Q4 - May 31."
  },
  
  pricing: {
    title: "TDS Filing Fees",
    description: "Quarterly TDS return filing services.",
    packages: [
      {
        name: "Per Quarter",
        price: "₹1,999",
        description: "Single quarter filing",
        features: [
          "Return preparation",
          "Portal filing",
          "Acknowledgment",
          "Form 16/16A (up to 10)",
          "Basic support"
        ]
      },
      {
        name: "Annual",
        price: "₹6,999",
        description: "All 4 quarters",
        features: [
          "All quarterly filings",
          "TDS rate advisory",
          "Form 16/16A generation",
          "Correction returns",
          "Year-round support"
        ],
        popular: true
      },
      {
        name: "Complete",
        price: "₹14,999",
        description: "TDS + Payroll",
        features: [
          "All quarterly filings",
          "Payroll processing",
          "Salary TDS calculation",
          "Investment declarations",
          "Full compliance"
        ]
      }
    ],
    governmentFees: "No filing fees. Interest/penalty if payment delayed."
  },
  
  faqs: [
    { question: "What are TDS return due dates?", answer: "Q1 (Apr-Jun): July 31, Q2 (Jul-Sep): October 31, Q3 (Oct-Dec): January 31, Q4 (Jan-Mar): May 31." },
    { question: "What forms are used for TDS returns?", answer: "24Q: Salary TDS, 26Q: Non-salary TDS (residents), 27Q: Non-salary (non-residents), 27EQ: TCS returns." },
    { question: "What is the penalty for late filing?", answer: "₹200 per day from due date until filing, subject to maximum of TDS amount. Plus interest on late payment." },
    { question: "Can I revise TDS return?", answer: "Yes, correction returns can be filed to fix errors. Online correction available for most changes. Statement type revision needs fresh return." },
    { question: "What if TDS is deducted but not deposited?", answer: "Deductor is liable to pay TDS with interest. Deductee cannot claim TDS credit until deposited. Prosecution possible for willful default." },
    { question: "When are Form 16/16A due?", answer: "Form 16 (salary): June 15 after FY end. Form 16A (non-salary): Within 15 days of return filing due date." }
  ],
  
  relatedServices: [
    { id: "income-tax-filing", label: "ITR Filing" },
    { id: "payroll-compliance", label: "Payroll Services" },
    { id: "gst-return-filing", label: "GST Returns" },
    { id: "annual-filing", label: "Annual Compliance" },
    { id: "virtual-cfo", label: "Virtual CFO" },
    { id: "tax-planning", label: "Tax Planning" }
  ],
  
  trustSignals: {
    incorporations: "15,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Tax Experts"
  }
};

// ==================== CXO SERVICES ====================

export const payrollCompliance = {
  id: "payroll-compliance",
  slug: "payroll-compliance",
  seoTitle: "Payroll Services India | Payroll Compliance Management",
  seoDescription: "Complete payroll processing and compliance services. Salary processing, PF, ESI, professional tax, TDS. Expert payroll management for businesses.",
  heroTitle: "Payroll Compliance Services",
  heroSubtitle: "CXO Services",
  summary: "Payroll Compliance involves processing employee salaries and ensuring compliance with all statutory requirements including PF, ESI, Professional Tax, TDS, Bonus, and Gratuity. Our comprehensive payroll services handle everything from salary calculation to statutory filings, ensuring your business stays compliant.",
  
  whoNeeds: {
    title: "Who Needs Payroll Services?",
    description: "Businesses with employees needing payroll processing and compliance.",
    points: [
      "Startups hiring their first employees",
      "SMEs wanting to outsource payroll",
      "Companies expanding their workforce",
      "Businesses with complex salary structures",
      "Companies with multi-location employees",
      "Firms needing PF/ESI compliance",
      "Businesses requiring statutory compliance",
      "Companies wanting to reduce HR overhead"
    ]
  },
  
  whyRequired: {
    title: "Why Professional Payroll Management?",
    description: "Payroll compliance has legal and employee satisfaction implications.",
    points: [
      "Legal Compliance: PF, ESI, PT are mandatory with penalties for default.",
      "Accuracy: Accurate salary calculation prevents employee disputes.",
      "Timeliness: Delayed salary affects employee morale and productivity.",
      "TDS Compliance: Correct TDS deduction prevents tax issues.",
      "Documentation: Proper records for audits and inspections.",
      "Cost Saving: Outsourcing cheaper than in-house payroll team."
    ]
  },
  
  benefits: [
    { title: "Salary Processing", description: "Monthly salary calculation and disbursement." },
    { title: "PF Compliance", description: "EPF registration, monthly filing, and payment." },
    { title: "ESI Compliance", description: "ESIC registration, contribution, and returns." },
    { title: "Professional Tax", description: "PT registration and monthly compliance." },
    { title: "TDS on Salary", description: "Monthly TDS calculation and quarterly returns." },
    { title: "Payslips", description: "Generate and distribute monthly payslips." },
    { title: "Form 16", description: "Annual Form 16 generation for employees." },
    { title: "Leave Management", description: "Track leaves and encashment." }
  ],
  
  documentsRequired: {
    title: "Information for Payroll Setup",
    description: "Details needed to set up payroll processing.",
    directors: [
      "Employer registration details",
      "Authorized signatory information",
      "Bank account for salary disbursement"
    ],
    company: [
      "Employee details (name, PAN, Aadhaar, bank)",
      "Salary structure and components",
      "PF and ESI registration numbers",
      "Professional Tax registration",
      "Leave policy details",
      "Attendance data (monthly)",
      "Investment declarations from employees"
    ]
  },
  
  process: [
    { step: 1, title: "Setup", description: "Configure payroll system with company and employee details.", duration: "3-5 days" },
    { step: 2, title: "Registration", description: "Complete PF, ESI, PT registrations if not done.", duration: "7-15 days" },
    { step: 3, title: "Monthly Input", description: "Collect attendance, leaves, and variable pay data.", duration: "Monthly" },
    { step: 4, title: "Processing", description: "Calculate salaries, deductions, and net pay.", duration: "1-2 days" },
    { step: 5, title: "Disbursement", description: "Process salary payments and distribute payslips.", duration: "1 day" },
    { step: 6, title: "Compliance", description: "File PF, ESI, PT returns and pay challans.", duration: "By due date" }
  ],
  
  timeline: {
    total: "Ongoing monthly service",
    breakdown: [
      { phase: "Initial setup", duration: "3-5 days" },
      { phase: "Monthly processing", duration: "3-5 days" },
      { phase: "Statutory filings", duration: "By due dates" }
    ],
    note: "Salary processed by 1st of month. Statutory due dates: PF - 15th, ESI - 15th, TDS - 7th."
  },
  
  pricing: {
    title: "Payroll Service Pricing",
    description: "Monthly pricing based on employee count.",
    packages: [
      {
        name: "Starter",
        price: "₹3,999/month",
        description: "Up to 10 employees",
        features: [
          "Salary processing",
          "Payslip generation",
          "PF & ESI filing",
          "TDS calculation",
          "Email support"
        ]
      },
      {
        name: "Growth",
        price: "₹7,999/month",
        description: "Up to 25 employees",
        features: [
          "All Starter features",
          "Professional Tax",
          "Leave management",
          "Reimbursements",
          "Dedicated manager"
        ],
        popular: true
      },
      {
        name: "Enterprise",
        price: "₹14,999/month",
        description: "Up to 50 employees",
        features: [
          "All Growth features",
          "Multi-location",
          "Bonus/Gratuity",
          "Full Form 16",
          "HR support"
        ]
      }
    ],
    governmentFees: "PF: 13% employer + 12% employee. ESI: 3.25% employer + 0.75% employee."
  },
  
  faqs: [
    { question: "What is included in payroll compliance?", answer: "Salary processing, PF contribution and filing, ESI contribution and filing, Professional Tax, TDS on salary, and Form 16 generation." },
    { question: "What are PF and ESI thresholds?", answer: "PF: Mandatory for establishments with 20+ employees. ESI: For employees earning up to ₹21,000/month in establishments with 10+ employees." },
    { question: "Can you handle variable pay and reimbursements?", answer: "Yes, we handle all salary components including fixed pay, variable pay, bonuses, reimbursements, and deductions." },
    { question: "How do employees access payslips?", answer: "Payslips can be emailed to employees or provided through employee self-service portal." },
    { question: "What if we have employees in multiple states?", answer: "We handle multi-state compliance including state-specific Professional Tax and Labor Welfare Fund requirements." },
    { question: "Can you help with PF/ESI registrations?", answer: "Yes, we handle new registrations for PF, ESI, and Professional Tax as part of our setup service." }
  ],
  
  relatedServices: [
    { id: "virtual-cfo", label: "Virtual CFO" },
    { id: "tds-filing", label: "TDS Filing" },
    { id: "compliance-manager", label: "Compliance Manager" },
    { id: "hr-consulting", label: "HR Services" },
    { id: "income-tax-filing", label: "Tax Filing" },
    { id: "private-limited-company", label: "Company Registration" }
  ],
  
  trustSignals: {
    incorporations: "500+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Payroll Experts"
  }
};

// Export all tax and CXO services
export const additionalTaxServices = {
  gstLutFiling,
  gstAnnualReturn,
  tdsFiling,
  payrollCompliance
};
